import React from 'react';
import styled from 'styled-components';
import { LeftContentContainerStyles } from '../../../styling/styles';
import BREAK_POINTS from 'packages/smart-home-support/styling/media-queries';
import SectionFrame, { SectionFrameProps } from './SectionFrame';
import { Subtitle, ListText, Heading2, TopSubhead } from 'packages/smart-home-support/components/Typography';
import { PartnerThemeProps } from 'packages/smart-home-support/themes';
import getPartnerAsset, { DynamicAssetsKeys } from 'packages/smart-home-support/assets';
import { ValuePropItem, ValuePropositionContent } from 'packages/smart-home-support/assets/models';
import { useTweekValue } from 'react-tweek';
import ScrollAnimationWrapper, {
  AnimationType,
  AnimatedListItem,
} from 'packages/smart-home-support/components/ScrollAnimationWrapper';

const SectionContainer = styled.div`
  margin: 0px 37px;

  @media ${BREAK_POINTS.tablet} {
    display: flex;
    flex-direction: ${(props: { flipped?: boolean }) => (props.flipped === true ? 'row' : 'row-reverse')};
  }
`;

interface ValuePropContainerProps {
  backgroundColorKey: number;
}

const ValuePropContainer = styled(SectionFrame)`
  flex: 5;
  display: flex;
  align-items: center;
  color: ${(props: ValuePropContainerProps & PartnerThemeProps) =>
    props.theme.colors[`featureSection${props.backgroundColorKey}FontColor`]};
  background-color: ${(props: ValuePropContainerProps & PartnerThemeProps) =>
    props.theme.colors[`featureSection${props.backgroundColorKey}`]};

  min-height: unset;

  @media ${BREAK_POINTS.mobileBig} {
    min-height: 400px;
  }

  @media ${BREAK_POINTS.tablet} {
    min-height: unset;
  }
`;

const ValuePropContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  color: inherit;

  ${LeftContentContainerStyles}
`;

const iconTitleComponents = {
  TitleContainer: styled.div`
    display: flex;
    align-items: center;

    margin: 15px;

    @media ${BREAK_POINTS.desktop} {
      margin: 30px;
    }
  `,
  Icon: styled.img`
    height: 50px;
  `,
  Title: styled(TopSubhead)`
    color: white;
    margin-left: 15px;
  `,
};

const IconTitle = ({ caption, src }: { caption: ValuePropItem; src: string }) => (
  <iconTitleComponents.TitleContainer>
    <iconTitleComponents.Icon src={src} />
    <iconTitleComponents.Title>{caption}</iconTitleComponents.Title>
  </iconTitleComponents.TitleContainer>
);

const ValueSubTitle = styled(Subtitle)`
  color: inherit;
`;

const valuePropPointsComponents = {
  ListContainer: styled.div`
    margin-top: 20px;
    margin-left: -20px;
  `,
  List: styled.ul``,
  ListItem: styled(ListText)`
    color: inherit;
    margin-bottom: 15px;
    animation-delay: inherit;
  `,
};

const ValuePropPoints = ({ points }: { points: (string | JSX.Element)[] }) => (
  <ScrollAnimationWrapper animationType={AnimationType.AnimatedList}>
    <valuePropPointsComponents.ListContainer>
      <valuePropPointsComponents.List>
        {points.map((point, i) => (
          <AnimatedListItem index={i} key={i}>
            <valuePropPointsComponents.ListItem>{point}</valuePropPointsComponents.ListItem>
          </AnimatedListItem>
        ))}
      </valuePropPointsComponents.List>
    </valuePropPointsComponents.ListContainer>
  </ScrollAnimationWrapper>
);

const Heading: React.FC<any> = getPartnerAsset(DynamicAssetsKeys.homeValuePropTitleComponent);
const ImageContainer: React.FC<any> = getPartnerAsset(DynamicAssetsKeys.homeValuePropImageContainer);
const contents: ValuePropositionContent[] = getPartnerAsset(DynamicAssetsKeys.homeValuePropContent);

const ValuePropositions = () => {
  let showRefundBanner: boolean = useTweekValue(
    'soluto_home_web/smart_home_support/pages/refunds/show_refund_banner',
    false
  );

  contents.map(
    (content: ValuePropositionContent, i) =>
      (content.shouldShowBanner = content.label?.includes('refund') ? showRefundBanner : true)
  );

  return (
    <>
      {contents.map((content: ValuePropositionContent, i) => (
        <SectionContainer key={i} flipped={i % 2 === 0}>
          {content.shouldShowBanner && (
            <>
              <ImageContainer src={content.image} {...content.imageMargins}>
                <IconTitle src={content.icon} caption={content.caption} />
              </ImageContainer>
              <ValuePropContainer backgroundColorKey={((i + 1) % 2) + 1} {...content.contentMargins}>
                <ValuePropContentContainer>
                  <ScrollAnimationWrapper animationType={AnimationType.TitleSlideUp}>
                    <Heading>{content.title}</Heading>
                  </ScrollAnimationWrapper>

                  <ScrollAnimationWrapper animationType={AnimationType.DefaultSlideUp}>
                    <ValueSubTitle>{content.subtitle}</ValueSubTitle>
                  </ScrollAnimationWrapper>

                  <ValuePropPoints points={content.points} />

                  <ScrollAnimationWrapper animationType={AnimationType.DefaultSlideUp}>
                    {content.link}
                  </ScrollAnimationWrapper>
                </ValuePropContentContainer>
              </ValuePropContainer>
            </>
          )}
        </SectionContainer>
      ))}
    </>
  );
};

export default ValuePropositions;



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/homepage/components/ValuePropositions.tsx